import { useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
// The module rather than the feature's index: this is the one reference list
// the brand screen and this one both draw from, and reaching for it through
// `../../brand` would pull that whole screen in behind it — the same reason
// `settingsService` reaches for `mediaUrl` the same way.
import { fetchPlanSuggestions } from "../../brand/services/planCatalogService";

export type SpecializationOptionsStatus = "loading" | "ready" | "failed";

export interface SpecializationOptionsState {
  /** The names `/api/v1/package-name-suggestions/` offers, for the field to suggest. */
  options: string[];
  status: SpecializationOptionsStatus;
}

const NO_OPTIONS: string[] = [];

/**
 * What the Specializations field suggests, read from the same reference list
 * the brand screen's create-plan dialog picks a plan name from —
 * `/api/v1/package-name-suggestions/` — rather than a list this screen keeps of
 * its own.
 *
 * A suggestion, not a closed set, exactly as `profileCodes` already treats
 * it: a name typed that this list has never heard of is still accepted, so a
 * failed read is survivable rather than something that has to block the
 * form. It falls back to nothing fetched and a toast, and the field stays
 * usable by typing straight into it.
 *
 * Fetched once when the form mounts rather than kept between visits — the
 * list is small, and a dietitian editing their profile again days later
 * should see whatever the backend suggests now, not what it suggested then.
 *
 * Must be used inside a Clerk provider and an active session: the endpoint is
 * 401 without a bearer token.
 */
export function useSpecializationOptions(): SpecializationOptionsState {
  const { getToken } = useAuth();
  const toast = useToast();

  // Clerk hands back a fresh `getToken` on some renders, and the load below
  // must not re-run for that — a fetch per render is a loop. The effect reads
  // the current one through this rather than depending on its identity.
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  const [options, setOptions] = useState<string[]>(NO_OPTIONS);
  const [status, setStatus] = useState<SpecializationOptionsStatus>("loading");

  useEffect(() => {
    const controller = new AbortController();

    fetchPlanSuggestions(() => getTokenRef.current(), controller.signal)
      .then((suggestions) => {
        // Unmounted before the read landed.
        if (controller.signal.aborted) return;

        setOptions(suggestions.map((suggestion) => suggestion.name));
        setStatus("ready");
      })
      .catch((error) => {
        if (controller.signal.aborted) return;

        console.error("specialization options failed to load:", error);
        setOptions(NO_OPTIONS);
        setStatus("failed");

        toast.error(
          "Could not load the specialization suggestions",
          error instanceof ApiError
            ? error.message
            : "We could not reach the server. Check your connection and try again."
        );
      });

    return () => controller.abort();
  }, [toast]);

  return { options, status };
}

export default useSpecializationOptions;
